import type { z } from 'zod';

import { env } from '@/lib/env';

import {
  ApiError,
  NetworkError,
  ResponseShapeError,
  apiErrorEnvelopeSchema,
} from '@/lib/api/errors';

/**
 * The single HTTP client for the whole app.
 *
 * Responsibilities, all in one place so no feature has to reimplement them:
 *
 * - Attaches the bearer token from a pluggable provider, so this module never
 *   imports Supabase and stays trivially testable.
 * - Validates every response against a Zod schema. An API contract change
 *   surfaces here as one clear error rather than as `undefined` rendered into
 *   the DOM three components deep.
 * - Normalises failures into `ApiError` / `NetworkError` / `ResponseShapeError`.
 * - Enforces a timeout, because `fetch` has none by default and a hung request
 *   would leave a spinner on screen forever.
 * - Retries idempotent requests on transient failures with jittered backoff.
 */

const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_RETRY_ATTEMPTS = 2;
const RETRYABLE_METHODS = new Set(['GET', 'HEAD']);

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/** Supplies the current access token, or null when signed out. */
export type TokenProvider = () => Promise<string | null> | string | null;

export interface RequestOptions<TSchema extends z.ZodType> {
  /** Schema the response body must satisfy. */
  schema: TSchema;
  method?: HttpMethod;
  body?: unknown;
  /**
   * Multipart form body. When set, `Content-Type` is left unset so the browser
   * (or undici) can attach the correct boundary — required for file uploads.
   */
  formData?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Send the bearer token. Defaults to true. */
  authenticated?: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
  retryAttempts?: number;
  /**
   * Non-2xx statuses whose body is still a valid, meaningful response.
   *
   * `GET /health/ready` is the motivating case: it answers 503 when a dependency
   * is down, but the body is a full readiness report that the status page needs
   * to render. Without this the client would throw away exactly the information
   * the caller asked for.
   */
  acceptStatuses?: readonly number[];
}

function buildQueryString(
  query: Record<string, string | number | boolean | undefined | null>,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised ? `?${serialised}` : '';
}

function jitteredBackoff(attempt: number): number {
  // Full jitter over an exponentially growing window, matching the backend's
  // policy so a struggling upstream is not hammered from both sides.
  const ceiling = Math.min(250 * 2 ** (attempt - 1), 4_000);
  return Math.random() * ceiling;
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export class ApiClient {
  private readonly baseUrl: string;
  private tokenProvider: TokenProvider;

  constructor(options: { baseUrl?: string; tokenProvider?: TokenProvider } = {}) {
    this.baseUrl = (options.baseUrl ?? env.VITE_API_BASE_URL).replace(/\/+$/, '');
    this.tokenProvider = options.tokenProvider ?? (() => null);
  }

  /**
   * Install the token source.
   *
   * Called once by the auth provider at startup. Injecting it this way keeps the
   * client free of any dependency on Supabase or React.
   */
  setTokenProvider(provider: TokenProvider): void {
    this.tokenProvider = provider;
  }

  async request<TSchema extends z.ZodType>(
    path: string,
    options: RequestOptions<TSchema>,
  ): Promise<z.infer<TSchema>> {
    const {
      schema,
      method = 'GET',
      body,
      formData,
      query,
      authenticated = true,
      signal,
      timeoutMs = DEFAULT_TIMEOUT_MS,
      retryAttempts = RETRYABLE_METHODS.has(method) ? DEFAULT_RETRY_ATTEMPTS : 0,
      acceptStatuses,
    } = options;

    if (body !== undefined && formData !== undefined) {
      throw new Error('RequestOptions cannot include both body and formData.');
    }

    const url = `${this.baseUrl}${path}${query ? buildQueryString(query) : ''}`;
    let lastError: unknown;

    for (let attempt = 1; attempt <= retryAttempts + 1; attempt += 1) {
      try {
        const response = await this.send({
          url,
          method,
          body,
          formData,
          authenticated,
          signal,
          timeoutMs,
          acceptStatuses,
        });
        return this.parse(schema, response.payload, response.status);
      } catch (error) {
        lastError = error;

        const isLastAttempt = attempt === retryAttempts + 1;
        const worthRetrying =
          error instanceof NetworkError ||
          (error instanceof ApiError && error.isRetryable && error.status !== 429);

        // A 429 is excluded above on purpose: the server has explicitly asked us
        // to back off, so retrying automatically would make things worse.
        if (isLastAttempt || !worthRetrying) throw error;
        await sleep(jitteredBackoff(attempt));
      }
    }

    throw lastError;
  }

  private async send(args: {
    url: string;
    method: HttpMethod;
    body: unknown;
    formData: FormData | undefined;
    authenticated: boolean;
    signal: AbortSignal | undefined;
    timeoutMs: number;
    acceptStatuses: readonly number[] | undefined;
  }): Promise<{ payload: unknown; status: number }> {
    const { url, method, body, formData, authenticated, signal, timeoutMs, acceptStatuses } = args;

    const headers = new Headers({ Accept: 'application/json' });
    // JSON only — multipart must not set Content-Type or the boundary is lost.
    if (body !== undefined) headers.set('Content-Type', 'application/json');

    if (authenticated) {
      const token = await this.tokenProvider();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }

    // Combine the caller's signal with our timeout so either can abort.
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    const composedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

    // `exactOptionalPropertyTypes` treats an explicit `body: undefined` as a
    // type error, so the key is omitted entirely when there is no body.
    const init: RequestInit = {
      method,
      headers,
      signal: composedSignal,
      credentials: 'omit',
    };
    if (formData !== undefined) init.body = formData;
    else if (body !== undefined) init.body = JSON.stringify(body);

    let response: Response;
    try {
      response = await fetch(url, init);
    } catch (error) {
      // A caller-initiated abort is not a failure; let it propagate untouched so
      // React Query treats it as a cancellation rather than an error state.
      if (signal?.aborted) throw error;
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new NetworkError(`The request to ${url} timed out.`, error);
      }
      throw new NetworkError('Network request failed.', error);
    }

    const payload = await this.readBody(response);
    const accepted = response.ok || (acceptStatuses?.includes(response.status) ?? false);
    if (!accepted) throw this.toApiError(response, payload);
    return { payload, status: response.status };
  }

  private async readBody(response: Response): Promise<unknown> {
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return null;
    }
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      // Non-JSON bodies (an HTML error page from a proxy, for instance) are
      // returned raw so the error path can still report something useful.
      return text;
    }
  }

  private toApiError(response: Response, payload: unknown): ApiError {
    const envelope = apiErrorEnvelopeSchema.safeParse(payload);
    if (envelope.success) {
      const { code, message, details, request_id } = envelope.data.error;
      return new ApiError({
        status: response.status,
        code,
        message,
        details,
        requestId: request_id,
      });
    }

    // Something in front of the API answered (gateway, proxy, CDN) and did not
    // use our envelope. Synthesise one so callers still get a consistent type.
    return new ApiError({
      status: response.status,
      code: `http_${String(response.status)}`,
      message:
        response.status >= 500
          ? 'The server is having trouble right now. Please try again shortly.'
          : 'The request could not be completed.',
      requestId: response.headers.get('X-Request-ID'),
    });
  }

  private parse<TSchema extends z.ZodType>(
    schema: TSchema,
    payload: unknown,
    status: number,
  ): z.infer<TSchema> {
    const result = schema.safeParse(payload);
    if (result.success) return result.data;

    const issues = result.error.issues.map(
      (issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`,
    );
    throw new ResponseShapeError(
      `The API response did not match the expected shape (HTTP ${String(status)}).`,
      issues,
    );
  }

  // ----------------------------------------------------------------------- //
  // Verb helpers
  // ----------------------------------------------------------------------- //
  get<TSchema extends z.ZodType>(
    path: string,
    options: Omit<RequestOptions<TSchema>, 'method' | 'body'>,
  ): Promise<z.infer<TSchema>> {
    return this.request(path, { ...options, method: 'GET' });
  }

  post<TSchema extends z.ZodType>(
    path: string,
    options: Omit<RequestOptions<TSchema>, 'method'>,
  ): Promise<z.infer<TSchema>> {
    return this.request(path, { ...options, method: 'POST' });
  }

  patch<TSchema extends z.ZodType>(
    path: string,
    options: Omit<RequestOptions<TSchema>, 'method'>,
  ): Promise<z.infer<TSchema>> {
    return this.request(path, { ...options, method: 'PATCH' });
  }

  delete<TSchema extends z.ZodType>(
    path: string,
    options: Omit<RequestOptions<TSchema>, 'method'>,
  ): Promise<z.infer<TSchema>> {
    return this.request(path, { ...options, method: 'DELETE' });
  }
}

/** The app-wide client instance. */
export const apiClient = new ApiClient();
